import { useEffect, useRef, useState } from 'react';
import { useCamera } from '../hooks/useCamera';
import IconButton from './IconButton';
import { CloseIcon } from './UIIcons';
import { safeTopPad, safeBottomPad } from '../lib/safeArea';

interface Props {
  /** Called once a still frame has been captured. The canvas contains the frame. */
  onCapture: (canvas: HTMLCanvasElement) => void;
  /** Optional cancel button — called when user wants to leave the capture screen. */
  onCancel?: () => void;
}

/**
 * Live camera preview with a single big "Capture" shutter button.
 * On capture, draws the current video frame onto an offscreen canvas at full
 * intrinsic resolution and hands it to the parent. The parent then takes over
 * (annotation, freezing, etc.) and the live stream is stopped to save battery.
 */
export default function CameraCapture({ onCapture, onCancel }: Props) {
  const { stream, status, error, start } = useCamera();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !stream) return;
    v.srcObject = stream;
    const onLoaded = () => setReady(true);
    v.addEventListener('loadedmetadata', onLoaded);
    void v.play().catch(() => {});
    return () => v.removeEventListener('loadedmetadata', onLoaded);
  }, [stream]);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !ready) return;
    const w = v.videoWidth;
    const h = v.videoHeight;
    if (!w || !h) return;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, w, h);
    onCapture(canvas);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#000',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        style={{
          flex: 1,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          background: '#000',
        }}
      />

      {/* Top bar — inline safe-area math, see lib/safeArea.ts. */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          paddingTop: safeTopPad(12),
          paddingBottom: 12,
          paddingLeft: 16,
          paddingRight: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))',
          pointerEvents: 'none',
        }}
      >
        <div style={{ pointerEvents: 'auto' }}>
          {onCancel && (
            <IconButton
              label="Cancel"
              variant="ghost"
              onClick={onCancel}
              icon={<CloseIcon />}
            />
          )}
        </div>
        <div
          style={{
            pointerEvents: 'auto',
            color: 'var(--text-dim)',
            fontSize: 13,
            background: 'var(--surface)',
            padding: '6px 12px',
            borderRadius: 999,
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
          }}
        >
          {status === 'live'
            ? 'Aim at the C-arm monitor'
            : status === 'requesting'
              ? 'Starting camera…'
              : status === 'error'
                ? 'Camera error'
                : 'Idle'}
        </div>
      </div>

      {/* Error overlay */}
      {status === 'error' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
            textAlign: 'center',
            gap: 16,
            background: 'rgba(0,0,0,0.85)',
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            Camera unavailable
          </div>
          <div style={{ color: 'var(--text-dim)', maxWidth: 320 }}>
            {error}
            <br />
            <br />
            On iPhone, ensure Safari has camera permission and the page is
            served over HTTPS.
          </div>
          <IconButton label="Try again" variant="primary" onClick={start} />
        </div>
      )}

      {/* Shutter */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          paddingBottom: safeBottomPad(24),
          background:
            'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0))',
        }}
      >
        <button
          type="button"
          aria-label="Capture frame"
          onClick={capture}
          disabled={!ready || status !== 'live'}
          style={{
            width: 84,
            height: 84,
            borderRadius: '50%',
            border: '4px solid white',
            background: 'rgba(255,255,255,0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: ready ? 1 : 0.5,
          }}
        >
          <span
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'white',
            }}
          />
        </button>
      </div>
    </div>
  );
}
